import React from "react";

const AboutUs = () => {
  return (
    <div className="bg-white">
      {/* About Section Header */}
      <div className="mb-8 text-center">
        <h2 className="text-4xl font-extrabold tracking-tight text-gray-900 uppercase md:text-5xl">
          About <span className="text-yellow-500">Shinpex Academy</span>
        </h2>
        <div className="w-24 h-1 mx-auto mt-2 bg-yellow-500"></div>
        <p className="max-w-2xl mx-auto mt-4 text-lg text-gray-600">
          Shaping the future of Africa through world-class education and innovation
        </p>
      </div>

      {/* Mission and Vision Grid */}
      <div className="grid grid-cols-1 gap-8 mb-12 md:grid-cols-2">
        {/* Mission Card */}
        <div id="mission" className="p-8 transition-all duration-300 transform shadow-xl bg-gradient-to-br from-green-700 to-green-800 rounded-xl hover:scale-105">
          <div className="flex items-center mb-4">
            <div className="flex items-center justify-center w-12 h-12 bg-yellow-500 rounded-full">
              <svg className="w-6 h-6 text-black" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
            </div>
            <h3 className="ml-4 text-2xl font-bold text-white uppercase">Our Mission</h3>
          </div>
          <p className="text-lg leading-relaxed text-gray-100">
            To provide world-class education that empowers African students with the knowledge, 
            skills, and values needed to become global leaders and innovators. We are committed 
            to reversing Africa's brain drain by creating an educational environment that rivals 
            the best institutions worldwide.
          </p>
        </div>

        {/* Vision Card */}
        <div id="vision" className="p-8 transition-all duration-300 transform shadow-xl bg-gradient-to-br from-yellow-500 to-yellow-600 rounded-xl hover:scale-105">
          <div className="flex items-center mb-4">
            <div className="flex items-center justify-center w-12 h-12 bg-black rounded-full">
              <svg className="w-6 h-6 text-yellow-500" fill="currentColor" viewBox="0 0 20 20">
                <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
                <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" />
              </svg>
            </div>
            <h3 className="ml-4 text-2xl font-bold text-black uppercase">Our Vision</h3>
          </div>
          <p className="text-lg leading-relaxed text-gray-900">
            To become Africa's premier educational institution, recognized globally for excellence 
            in academics, research, and character development. We envision a future where African 
            students choose to stay and contribute to their continent's development rather than 
            seeking opportunities elsewhere.
          </p>
        </div>
      </div>

      {/* Core Values Section */}
      <div className="p-8 bg-gray-50 rounded-xl">
        <h3 className="mb-8 text-3xl font-bold text-center text-gray-900 uppercase">
          Our Core Values
        </h3>
        <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
          {/* Excellence */}
          <div className="text-center">
            <div className="flex items-center justify-center w-16 h-16 mx-auto mb-4 bg-yellow-500 rounded-full">
              <svg className="w-8 h-8 text-black" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
            </div>
            <h4 className="mb-2 text-xl font-bold text-gray-900">Excellence</h4>
            <p className="text-gray-600">
              We strive for the highest standards in everything we do, from academics to character development.
            </p>
          </div>

          {/* Innovation */}
          <div className="text-center">
            <div className="flex items-center justify-center w-16 h-16 mx-auto mb-4 bg-green-700 rounded-full">
              <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z" clipRule="evenodd" />
              </svg>
            </div>
            <h4 className="mb-2 text-xl font-bold text-gray-900">Innovation</h4>
            <p className="text-gray-600">
              We embrace creativity and forward-thinking approaches to prepare students for tomorrow's challenges.
            </p>
          </div>

          {/* Integrity */}
          <div className="text-center">
            <div className="flex items-center justify-center w-16 h-16 mx-auto mb-4 bg-gray-800 rounded-full">
              <svg className="w-8 h-8 text-yellow-500" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
            </div>
            <h4 className="mb-2 text-xl font-bold text-gray-900">Integrity</h4>
            <p className="text-gray-600">
              We build character through honesty, transparency, and ethical leadership in all our endeavors.
            </p>
          </div>
        </div>
      </div>

      {/* Location and Contact Info */}
      <div className="p-8 mt-12 text-white bg-gradient-to-r from-gray-900 to-gray-800 rounded-xl">
        <div className="text-center">
          <h3 className="mb-4 text-2xl font-bold uppercase">Located in Ilorin, Kwara State</h3>
          <p className="mb-6 text-lg text-gray-300">
            Strategically positioned in the heart of Nigeria, we serve as a beacon of educational excellence
            for students across West Africa and beyond.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <div className="flex items-center">
              <svg className="w-5 h-5 mr-2 text-yellow-500" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
              </svg>
              <span>Ilorin, Kwara State, Nigeria</span>
            </div>
            <div className="flex items-center">
              <svg className="w-5 h-5 mr-2 text-yellow-500" fill="currentColor" viewBox="0 0 20 20">
                <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
              </svg>
              <span>Contact us for enrollment</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AboutUs;